'use client'

import * as React from 'react'
import * as Popover from '@radix-ui/react-popover'
import { Globe, Check } from 'lucide-react'
import { useI18n } from '@/lib/i18n'
import type { UiLanguage } from '@/lib/types'
import { cn } from '@/lib/utils'

const LANGS: Array<{ id: UiLanguage; label: string; native: string }> = [
  { id: 'cs', label: 'CZ', native: 'Čeština' },
  { id: 'en', label: 'EN', native: 'English' },
]

export function LanguageToggle() {
  const { uiLanguage, setUiLanguage, t } = useI18n()
  const [open, setOpen] = React.useState(false)

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button
          className={cn(
            'inline-flex h-9 w-9 items-center justify-center rounded-full',
            'text-muted-foreground transition-colors hover:text-foreground hover:bg-muted',
            'focus:outline-none focus:ring-2 focus:ring-ring',
            open && 'bg-muted text-foreground'
          )}
          aria-label="Regional settings"
          title="Regional settings"
        >
          <Globe className="h-5 w-5" />
        </button>
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content
          align="end"
          sideOffset={8}
          className={cn(
            'z-50 w-52 rounded-xl border border-border bg-popover shadow-xl',
            'animate-in fade-in-0 zoom-in-95 slide-in-from-top-2'
          )}
        >
          {/* Header */}
          <div className="px-4 pt-3 pb-2 border-b border-border/50">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              {t('regionalSettings.language', 'Language')}
            </p>
          </div>

          {/* Options */}
          <div className="p-1.5">
            {LANGS.map((lang) => {
              const isActive = uiLanguage === lang.id
              return (
                <button
                  key={lang.id}
                  onClick={() => { setUiLanguage(lang.id); setOpen(false) }}
                  className={cn(
                    'w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors',
                    isActive
                      ? 'bg-primary/8 text-foreground font-medium'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  )}
                >
                  <span className="flex items-center gap-2">
                    <span className="font-mono text-xs font-bold text-muted-foreground/70 w-6">{lang.label}</span>
                    <span>{lang.native}</span>
                  </span>
                  {isActive && <Check className="h-3.5 w-3.5 text-primary shrink-0" />}
                </button>
              )
            })}
          </div>

          {/* Currency — placeholder for future */}
          {/* 
          <div className="px-1.5 pb-1.5 border-t border-border/50 mt-1 pt-1.5">
            <p className="px-3 py-1 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Currency</p>
          </div>
          */}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  )
}
